import {
  ArrowLeft, BookOpen, ChevronRight, Pencil,
} from 'lucide-react-native';
import React, { useMemo } from 'react';
import { useNavigation } from '@react-navigation/native';
import type { NavigationProp } from '@react-navigation/native';
import {
  Badge, BadgeText, Box, Center, HStack, Heading, Icon, Pressable, ScrollView, Spinner, Text, VStack,
} from '@gluestack-ui/themed';
import { useLecturerModuleData } from '@/common/hooks/lecturer/useLecturerModuleData';
import { getLatestVersion } from '@/common/navigation/RoleNavigations/Lecturer/utils/navigationUtils';
import type { LecturerModule, LecturerRouteParamList } from '@/common/navigation/RoleNavigations/Lecturer/types';

const SCROLL_VIEW_CONTENT_STYLE = { flexGrow: 1 };

interface ModulePickerProps {
  onBack?: () => void;
}

/**
 * ModulePicker displays a list of modules for the user to select for editing.
 */
const ModulePicker: React.FC<ModulePickerProps> = ({ onBack }) => {
  const navigation = useNavigation<NavigationProp<LecturerRouteParamList>>();
  const { modules, isModulesLoading } = useLecturerModuleData(undefined, undefined, undefined);

  const sortedModules = useMemo(() => {
    if (!modules) return [];
    return [...modules].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  }, [modules]);

  const handleSelectModule = (module: LecturerModule) => {
    const targetVersion = getLatestVersion(module);
    navigation.navigate('ModuleEditor', {
      moduleId: module.id,
      version: targetVersion,
      taskId: null,
    });
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigation.navigate({ name: 'EvaluationDesk', params: {}, merge: false });
    }
  };

  if (isModulesLoading) {
    return (
      <Center flex={1}>
        <VStack space="md" alignItems="center">
          <Spinner size="large" />
          <Text color="$textSubtle">Loading modules...</Text>
        </VStack>
      </Center>
    );
  }

  if (!modules || modules.length === 0) {
    return (
      <Center flex={1} p="$8">
        <VStack space="xl" alignItems="center" maxWidth={400}>
          <Icon as={BookOpen} size="xl" color="$textHint" />
          <VStack space="xs" alignItems="center">
            <Heading size="md" color="$textHeading">No Modules Found</Heading>
            <Text size="sm" color="$textSubtle" textAlign="center">
              You don&apos;t have any modules to edit yet.
            </Text>
          </VStack>
        </VStack>
      </Center>
    );
  }

  return (
    <Box flex={1} bg="$bgApp">
      <ScrollView contentContainerStyle={SCROLL_VIEW_CONTENT_STYLE}>
        <Box p="$8" maxWidth={800} mx="auto" w="100%">
          <VStack space="xl">
            {/* Header with Back button */}
            <VStack space="lg">
              <Pressable
                onPress={handleBack}
                p="$2"
                ml="-$2"
                alignSelf="flex-start"
                borderRadius="$full"
                $hover={{ bg: '$bgHover' }}
              >
                <Icon as={ArrowLeft} size="md" color="$textSubtle" />
              </Pressable>
              <VStack space="xs">
                <HStack alignItems="center" space="sm">
                  <Icon as={Pencil} size="md" color="$brandBase" />
                  <Heading size="xl" color="$textHeading">Select a Module to Edit</Heading>
                </HStack>
                <Text color="$textSubtle">
                  Choose a module from the list below to open it in the editor.
                </Text>
              </VStack>
            </VStack>

            {/* Module List */}
            <VStack
              bg="$bgCard"
              borderRadius="$lg"
              borderWidth={1}
              borderColor="$borderLight"
              overflow="hidden"
            >
              {sortedModules.map((module, index) => {
                const isDraft = module.state?.workflowState === 'draft';
                const isPending = module.state?.workflowState === 'pending_approval';

                return (
                  <Pressable
                    key={module.id}
                    onPress={() => handleSelectModule(module)}
                    px="$5"
                    py="$4"
                    borderBottomWidth={index < sortedModules.length - 1 ? 1 : 0}
                    borderBottomColor="$borderLight"
                    $hover={{
                      backgroundColor: '$bgHover',
                    }}
                  >
                    <HStack justifyContent="space-between" alignItems="center">
                      <HStack space="md" alignItems="center" flex={1}>
                        <Box
                          w={40}
                          h={40}
                          borderRadius="$md"
                          bg="$bgBrandSubtle"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Icon as={BookOpen} size="sm" color="$brandBase" />
                        </Box>
                        <VStack flex={1}>
                          <Text size="md" fontWeight="medium" color="$textHeading" numberOfLines={1}>
                            {module.title}
                          </Text>
                          <HStack space="sm" alignItems="center">
                            <Text size="xs" color="$textSubtle">
                              v
                              {module.version || '0.0'}
                            </Text>
                            {isDraft && (
                              <Badge size="sm" variant="solid" action="warning" borderRadius="$sm">
                                <BadgeText>Draft</BadgeText>
                              </Badge>
                            )}
                            {isPending && (
                              <Badge size="sm" variant="solid" action="info" borderRadius="$sm">
                                <BadgeText>Pending</BadgeText>
                              </Badge>
                            )}
                          </HStack>
                        </VStack>
                      </HStack>
                      <Icon as={ChevronRight} size="sm" color="$textHint" />
                    </HStack>
                  </Pressable>
                );
              })}
            </VStack>
          </VStack>
        </Box>
      </ScrollView>
    </Box>
  );
};

export default ModulePicker;
